import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@cargomatic/ui';
import useUnitCalculation from 'components/v2/hooks/useUnitCalculation';
import {
  appliesToOptions,
  UNIT_CALC_HIDDEN,
  UNIT_CALC_PROMPT,
} from 'utils/accessorials/unitCalculation';

/**
 * The Unit Calculation section of the Edit Accessorial modal. Stateless: the
 * parent owns the accessorial and passes the change-handler factories it
 * already uses for the rest of the form. All derived view state comes from
 * `useUnitCalculation`.
 */
function UnitCalculationSection({
  accessorial,
  onSwitchChange,
  onSelectChange,
  onInputChange,
}) {
  const { t } = useTranslation();
  const view = useUnitCalculation(accessorial);

  if (view.mode === UNIT_CALC_HIDDEN) return null;

  if (view.mode === UNIT_CALC_PROMPT) {
    const isMissingBegin = view.missingDateField === 'begin_date_required';
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
        <span className="text-muted-foreground">
          {isMissingBegin
            ? t(
                'ADD_BEGIN_DATE_TO_ENABLE_UNIT_CALCULATION',
                'Add Begin Date to enable unit calculation.',
              )
            : t(
                'ADD_END_DATE_TO_ENABLE_UNIT_CALCULATION',
                'Add End Date to enable unit calculation.',
              )}
        </span>
        <button
          type="button"
          onClick={() => onSwitchChange(view.missingDateField)(true)}
          className="shrink-0 text-sm font-medium text-brand underline underline-offset-2 hover:opacity-80"
        >
          {isMissingBegin
            ? t('TURN_ON_BEGIN_DATE', 'Turn on Begin Date')
            : t('TURN_ON_END_DATE', 'Turn on End Date')}
        </button>
      </div>
    );
  }

  const { auto_calculate_units, applies_to, decimal_places, minimum_units } =
    accessorial;
  const { selectedMethod, preview, showDecimalPlaces } = view;

  return (
    <div className="rounded-lg border border-border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-foreground">
          {t('UNIT_CALCULATION', 'Unit Calculation')}
        </Label>
        <div className="flex items-center gap-2.5 shrink-0">
          <Label
            htmlFor="auto-calculate-units"
            className="text-sm font-normal text-foreground cursor-pointer"
          >
            {t('AUTO_CALCULATE_UNITS', 'Auto-calculate units')}
          </Label>
          <Switch
            id="auto-calculate-units"
            checked={auto_calculate_units}
            onCheckedChange={onSwitchChange('auto_calculate_units')}
          />
        </div>
      </div>

      {auto_calculate_units ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="calc-method"
                className="text-xs font-medium text-foreground"
              >
                {t('CALCULATION_METHOD', 'Calculation Method')}
              </Label>
              <Input
                id="calc-method"
                type="text"
                readOnly
                value={selectedMethod ? selectedMethod.label : ''}
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  'CALCULATION_METHOD_HELP',
                  'Determined by Rate Type and Unit Type.',
                )}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label
                htmlFor="applies-to"
                className="text-xs font-medium text-foreground"
              >
                {t('APPLIES_TO', 'Applies To')}
              </Label>
              <Select
                value={applies_to}
                onValueChange={onSelectChange('applies_to')}
              >
                <SelectTrigger id="applies-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {appliesToOptions.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t(
                  'APPLIES_TO_HELP',
                  'Calculated unit fields are read-only when this accessorial is added.',
                )}
              </p>
            </div>
          </div>

          {showDecimalPlaces && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="decimal-places"
                  className="text-xs font-medium text-foreground"
                >
                  {t('DECIMAL_PLACES', 'Decimal Places')}
                </Label>
                <Input
                  id="decimal-places"
                  type="number"
                  min="0"
                  max="10"
                  value={decimal_places ?? ''}
                  onChange={onInputChange('decimal_places', 'number')}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-foreground">
              {t('SOURCE_FIELDS', 'Source Fields')}
            </Label>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{t('BEGIN_DATE')}</Badge>
              <Badge variant="secondary">{t('END_DATE')}</Badge>
            </div>
          </div>

          {preview && (
            <div className="bg-muted rounded-md px-4 py-3 space-y-1.5">
              <p className="text-xs font-semibold text-foreground">
                {t('CALCULATION_PREVIEW', 'Calculation preview')}
              </p>
              <p className="text-xs text-muted-foreground">{preview.example}</p>
              <p className="text-xs text-muted-foreground/70 border-t border-border pt-1.5 mt-1.5">
                {preview.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label
                htmlFor="minimum-units"
                className="text-xs font-medium text-foreground"
              >
                {t('MINIMUM_UNITS', 'Minimum Units')}
              </Label>
              <Input
                id="minimum-units"
                type="number"
                value={minimum_units ?? ''}
                placeholder={t('NONE', 'None')}
                onChange={onInputChange('minimum_units', 'number')}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-muted rounded-md px-4 py-3">
          <p className="text-xs text-muted-foreground">
            Units are entered manually.
          </p>
        </div>
      )}
    </div>
  );
}

UnitCalculationSection.propTypes = {
  accessorial: PropTypes.shape({
    rate_type: PropTypes.string,
    unit_type: PropTypes.string,
    begin_date_required: PropTypes.bool,
    end_date_required: PropTypes.bool,
    auto_calculate_units: PropTypes.bool,
    calc_method: PropTypes.string,
    applies_to: PropTypes.string,
    decimal_places: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minimum_units: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onSwitchChange: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
};

export default UnitCalculationSection;
